import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// ============================================================================
// CONTEXT
// ============================================================================
const ParametresComptaContext = createContext(null);

// ============================================================================
// PROVIDER
// ============================================================================
export const ParametresComptaProvider = ({ children }) => {
  const [params, setParams] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);

  // Chargement initial
  useEffect(() => {
    loadParams();
  }, []);

  const loadParams = async () => {
    try {
      // Init si premier accès (INSERT IGNORE, pas de doublon)
      await api.parametresCompta.init();

      const r = await api.parametresCompta.getAll();
      if (r.success) {
        const flat = {};
        for (const [, values] of Object.entries(r.data || {})) {
          for (const [key, val] of Object.entries(values)) flat[key] = val;
        }
        setParams(flat);
      }
    } catch (e) {
      console.error('❌ [ParamsCompta] Erreur chargement:', e);
    } finally {
      setIsLoaded(true);
    }
  };

  // Rafraîchir depuis l'API (appelé après sauvegarde dans ParametresComptabilite)
  const refresh = useCallback(async () => {
    try {
      const r = await api.parametresCompta.getAll();
      if (r.success) {
        const flat = {};
        for (const [, values] of Object.entries(r.data || {})) {
          for (const [key, val] of Object.entries(values)) flat[key] = val;
        }
        setParams(flat);
      }
    } catch (e) {
      console.error('❌ [ParamsCompta] Erreur refresh:', e);
    }
  }, []);

  // ============================================================================
  // UTILITAIRES
  // ============================================================================

  /**
   * Formate un montant selon la devise configurée
   * formatMontant(23500)       → "23 500 FCFA"  (après chargement API)
   * formatMontant(23500)       → "23 500"        (avant chargement API)
   * formatMontant(23500, false) → "23 500"       (sans symbole)
   */
  const formatMontant = useCallback((valeur, avecSymbole = true) => {
    const v = parseFloat(valeur || 0);
    const decimales = parseInt(params.decimales) || 0;
    const sep = params.separateur_milliers || ' ';
    const symbole = params.symbole_devise || '';

    // Formater le nombre
    const parts = v.toFixed(decimales).split('.');
    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const formatted = decimales > 0 && parts[1] ? `${intPart},${parts[1]}` : intPart;

    return avecSymbole && symbole ? `${formatted} ${symbole}` : formatted;
  }, [params.decimales, params.separateur_milliers, params.symbole_devise]);

  /**
   * Formate un montant en version courte pour les KPIs
   * formatMontantCourt(12450000) → "12,5M FCFA"  (après chargement API)
   * formatMontantCourt(12450000) → "12,5M"        (avant chargement API)
   * formatMontantCourt(23500)    → "23 500 FCFA"
   */
  const formatMontantCourt = useCallback((valeur, avecSymbole = true) => {
    const v = parseFloat(valeur || 0);
    const symbole = params.symbole_devise || '';

    let formatted;
    if (v >= 10000000) {
      formatted = `${(v / 1000000).toFixed(1)}M`;
    } else {
      const sep = params.separateur_milliers || ' ';
      formatted = Math.round(v).toString().replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    }

    return avecSymbole && symbole ? `${formatted} ${symbole}` : formatted;
  }, [params.separateur_milliers, params.symbole_devise]);

  /**
   * Récupérer un paramètre avec valeur par défaut
   */
  const getParam = useCallback((cle, defaut = null) => {
    const val = params[cle];
    if (val === undefined || val === null) return defaut;
    return val;
  }, [params]);

  /**
   * Récupérer un booléen
   */
  const getBool = useCallback((cle, defaut = false) => {
    const val = params[cle];
    if (val === undefined || val === null) return defaut;
    return val === true || val === 'true';
  }, [params]);

  /**
   * Récupérer un nombre
   */
  const getNumber = useCallback((cle, defaut = 0) => {
    const val = params[cle];
    if (val === undefined || val === null) return defaut;
    return parseFloat(val) || defaut;
  }, [params]);

  // ============================================================================
  // VALUE
  // ============================================================================
  const value = {
    // Données brutes
    params,
    isLoaded,

    // Raccourcis fréquents
    devise: params.devise || '',
    symbole: params.symbole_devise || '',
    decimales: parseInt(params.decimales) || 0,

    // Fonctions
    formatMontant,
    formatMontantCourt,
    getParam,
    getBool,
    getNumber,
    refresh,
  };

  return (
    <ParametresComptaContext.Provider value={value}>
      {children}
    </ParametresComptaContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================
export const useParamsCompta = () => {
  const context = useContext(ParametresComptaContext);
  if (!context) {
    // Fallback si utilisé en dehors du provider (sécurité)
    return {
      params: {},
      isLoaded: false,
      devise: '',
      symbole: '',
      decimales: 0,
      formatMontant: (v) => `${parseFloat(v || 0).toLocaleString('fr-FR')}`,
      formatMontantCourt: (v) => `${parseFloat(v || 0).toLocaleString('fr-FR')}`,
      getParam: (_, d) => d,
      getBool: (_, d) => d || false,
      getNumber: (_, d) => d || 0,
      refresh: () => {},
    };
  }
  return context;
};

export default ParametresComptaContext;